import {AfterViewInit, Component, OnInit, ViewChild} from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSort } from '@angular/material/sort';
import { TranslateHelperService } from '../../services/TranslateService';
import { forkJoin } from 'rxjs';
import { User } from 'src/app/models/user';
import { AppState } from 'src/app/services/app.service';
import { DocumentsService } from 'src/app/services/documents.service';
import { UserService } from 'src/app/dataServices/userService';
import { LanguageService } from 'src/app/services/language.service';
import { DocumentModel } from './documents.model';
import { ContactInfo, SubdomainConfigService } from 'src/app/services/subdomainConfig.service';

@Component({
    selector: 'self-ins-documents',
    templateUrl: './documents-page.component.html',
    styleUrls: ['./documents-page.component.scss'],
    standalone: false
})

export class DocumentsPageComponent implements OnInit, AfterViewInit {
    @ViewChild(MatSort) public sort: MatSort | undefined;
    public user!: User;
    public documents = new MatTableDataSource<DocumentModel>([]);
    public gridFilterValue = '';
    public docCols: string[] = ['fileName', 'category', 'links'];
    public qnum = 0;
    public qsuffix = '';
    public policyDocs = true;
    public insuredDocs = true;
    public currentLocale: string = 'en-GB';
    public dateFormat: string = 'dd/MM/y';
    public email: string = '';
    public supportEmail: string = '';
    constructor(private activeRouter: ActivatedRoute,
        private router: Router,
        private appState: AppState,
        private documentsDataService: DocumentsService,
        private userService: UserService,
        private languageService: LanguageService,
        public _translateService: TranslateHelperService,
        private subdomainConfigService: SubdomainConfigService) {
        this.user = this.appState.user.getValue();
        this.currentLocale = this.languageService.getCurrentLocale();
        this.dateFormat = this.languageService.getShortDateFormat();
        
        const contactInfo: ContactInfo = this.subdomainConfigService.getContactInfo();
        this.email = contactInfo.email;
        this.supportEmail = contactInfo.supportEmail;
        
        this.activeRouter.queryParams.subscribe(params => {
            this.qnum = params['qnum'];
            this.qsuffix = params['qsuffix'];

            forkJoin([
                this.documentsDataService.getDocuments('FILEREG', Number(this.qnum).toFixed(2)),
                this.documentsDataService.getDocuments('INSUREDS', Number(this.qnum).toFixed(2)),
                this.userService.getQuoteByQnumQsuffix(this.qnum, this.qsuffix),
            ]).subscribe({
                    next: (data) => {
                        const [policyDocs, insuredDocs, quote] = data;
                        
                        let quoteSource = quote.polNum ? quote.polNum : this._translateService.translate(this._translateService.Keys.DOCUMENTS_QUOTE);

                        policyDocs.map(obj => {
                            obj.documentType = 'FILEREG';
                            obj.source = quoteSource;
                        });

                        insuredDocs.map(obj => {
                            obj.documentType = 'INSURED',
                            obj.source = this._translateService.translate(this._translateService.Keys.DOCUMENTS_INSURED)
                        });
                        
                        this.documents.data = policyDocs.concat(insuredDocs);

                        for (const doc of this.documents.data) {
                            const isDraft = doc.folder === 'D';
                            doc.iconClass = this.documentsDataService.getIconForDocument(
                                doc.fileLocation,
                                doc.subTable,
                                isDraft
                            );
                        }
                    }
            });
        });
    }

    ngOnInit() {
        this.documents.sortingDataAccessor = (item, property) => {
            if (property === 'type') {
                return item.iconClass;
            }
            if (property === 'fileName') {
                return item.description;
            }
            else {
                // @ts-ignore
                return item[property];
            }
        };
        this.documents.filterPredicate =
            (data: DocumentModel, filter: string) => {
                if (!filter) return true;

                if (filter === 'FILEREG' || filter === 'INSURED') {
                    return data.documentType === filter;
                }

                const searchStr = filter.toLowerCase();
                return (data.description?.toLowerCase().includes(searchStr) || false) ||
                       (data.source?.toLowerCase().includes(searchStr) || false) ||
                       (data.fileLocation?.toLowerCase().includes(searchStr) || false);
            };
    }

    ngAfterViewInit() {
        this.documents.sort = this.sort || null;
    }

    public toggleDocTypes(type: string, hide: boolean): void {
        this.documents.filter = !hide && type || '';
    }

    public download(fileLocation: string, type: string, id: number) {
        switch (type) {
            case 'jetfileicon jetfileicon-email':
            case 'jetfileicon jetfileicon-note':
            case 'jetfileicon jetfileicon-new disabled':
                break;
            case 'jetfileicon jetfileicon-video':
            case 'jetfileicon jetfileicon-office-powerpoint':
            case 'jetfileicon jetfileicon-office-word':
            case 'jetfileicon jetfileicon-office-excel':
            case 'jetfileicon jetfileicon-pdf':
            case 'jetfileicon jetfileicon-image-document':
            case 'jetfileicon jetfileicon-text-document':
                    this.documentsDataService
                    .getDocumentContent(id)
                    .subscribe((data: any) => {
                        const urlCreator = window.URL || (window as any).webkitURL;
                        const blob = new Blob([data]);
                        const fileUrl = urlCreator.createObjectURL(blob);
                        const result = (fileLocation || 'unknown').match(/[^/]*$/g);
                        const fileName = result ? result.shift() : null;
                        
                        const a = document.createElement('a');
                        document.body.appendChild(a);
                        a.href = fileUrl;
                        a.download = fileName as string;
                        a.click();
                        window.URL.revokeObjectURL(fileUrl);
                    });

                break;
            default:
                break;
        }
    }

    public downloadAll() {
        this.documentsDataService
            .getDocumentsArchive('FILEREG', Number(this.qnum).toFixed(2))
            .subscribe((data: any) => {
                const urlCreator = window.URL || (window as any).webkitURL;
                const blob = new Blob([data]);
                const fileUrl = urlCreator.createObjectURL(blob);
                const fileName = 'FILEREG - ' + this.qnum + ' documents.zip';
                
                const a = document.createElement('a');
                document.body.appendChild(a);
                a.href = fileUrl;
                a.download = fileName as string;
                a.click();
                window.URL.revokeObjectURL(fileUrl);
            });
    }

    public gotoHome() {
        this.router.navigate(['/coverage']);
    }

    public applyFilter() {
        this.documents.filter = this.gridFilterValue.trim().toLowerCase();
    }

    public clearSearch() {
        this.gridFilterValue = '';
        this.applyFilter();
    }
}
